import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type { Agency } from "../types";
import {
  counterLabel,
  generateGrantId,
  GRANT_COUNTER,
  GRANT_KINDS,
  grantAction,
  grantAgencyFilms,
  grantSummary,
  MAX_GRANT_FILMS,
  studioBillingErrorMessage,
  studioBillingKeys,
  type StudioGrantKind,
  type StudioGrantRequest,
  type StudioGrantResult,
  type StudioWalletCounters,
} from "./studioBillingClient";

const MAX_REASON_LENGTH = 500;

type Step = "form" | "confirm" | "done";

/**
 * Administrator-only: credit films to an agency by hand (a comp, goodwill, an
 * invoiced deal) through the bot's audited operator grant. Three steps: the
 * form, a confirm step that says exactly what will happen, and the wallet the
 * bot returns. One grant id per grant: a retry (or Back, then Review with the
 * same inputs) reuses it, so the bot replays instead of adding films twice.
 */
export const GrantFilmsDialog = ({
  agency,
  open,
  onOpenChange,
}: {
  agency: Agency;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const agencyId = String(agency.id);
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("form");
  const [films, setFilms] = useState("1");
  const [kind, setKind] = useState<StudioGrantKind>("comp");
  const [reason, setReason] = useState("");
  const [grant, setGrant] = useState<StudioGrantRequest | null>(null);
  const [result, setResult] = useState<StudioGrantResult | null>(null);

  const mutation = useMutation({
    mutationFn: (request: StudioGrantRequest) =>
      grantAgencyFilms(agencyId, request),
    onSuccess: (granted) => {
      setResult(granted);
      setStep("done");
      queryClient.invalidateQueries({
        queryKey: studioBillingKeys.agency(agencyId),
      });
    },
  });

  const filmCount = /^\d+$/.test(films.trim()) ? Number(films.trim()) : NaN;
  const filmsValid =
    Number.isInteger(filmCount) &&
    filmCount >= 1 &&
    filmCount <= MAX_GRANT_FILMS;
  const reasonValid =
    reason.trim().length > 0 && reason.trim().length <= MAX_REASON_LENGTH;
  const counter = GRANT_COUNTER[kind];

  const reset = () => {
    setStep("form");
    setFilms("1");
    setKind("comp");
    setReason("");
    // A failed attempt may still have reached the bot: keep its grant, so the
    // same inputs reviewed again reuse its id (a replay, never a double).
    if (!mutation.isError) setGrant(null);
    setResult(null);
    mutation.reset();
  };

  const close = () => {
    onOpenChange(false);
    reset();
  };

  const review = () => {
    if (!filmsValid || !reasonValid) return;
    const next = {
      films: filmCount,
      kind,
      counter,
      reason: reason.trim(),
    };
    // Same inputs as the grant already prepared (Back after a failed attempt):
    // keep its id, so a grant the bot did apply is replayed, not doubled.
    const same =
      grant &&
      grant.films === next.films &&
      grant.kind === next.kind &&
      grant.reason === next.reason;
    setGrant(
      same ? grant : { ...next, grant_id: generateGrantId(kind, agency) },
    );
    mutation.reset();
    setStep("confirm");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Grant films to {agency.name}</DialogTitle>
          <DialogDescription>
            Comps and goodwill go to <strong>free films</strong>: they are spent
            first and never unlock &ldquo;Powered by&rdquo; removal. Invoiced
            deals go to <strong>bought films</strong>, the same counter as a
            paid pack (bought films never expire and can unlock &ldquo;Powered
            by&rdquo; removal). Every grant is audited under your CRM e-mail,
            with the reason.
          </DialogDescription>
        </DialogHeader>

        {step === "form" && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="grant-films">Films</Label>
              <Input
                id="grant-films"
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_GRANT_FILMS}
                step={1}
                value={films}
                onChange={(event) => setFilms(event.target.value)}
                className="w-32"
              />
              {films && !filmsValid && (
                <p className="text-sm text-destructive">
                  A whole number from 1 to {MAX_GRANT_FILMS}.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Kind</span>
              <div className="flex flex-wrap items-center gap-2">
                {GRANT_KINDS.map((option) => (
                  <Button
                    key={option.id}
                    type="button"
                    size="sm"
                    variant={kind === option.id ? "default" : "outline"}
                    aria-pressed={kind === option.id}
                    onClick={() => setKind(option.id)}
                    title={option.hint}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {GRANT_KINDS.find((option) => option.id === kind)?.hint} Goes to{" "}
                {counterLabel(counter)} films.
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="grant-reason">Reason</Label>
              <Textarea
                id="grant-reason"
                placeholder="Why: the deal, the ticket or the failed film"
                value={reason}
                maxLength={MAX_REASON_LENGTH}
                onChange={(event) => setReason(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Required. Stored on the grant row with your e-mail.
              </p>
            </div>
          </div>
        )}

        {step === "confirm" && grant && (
          <div className="flex flex-col gap-3">
            <Alert>
              <AlertDescription className="text-base font-medium text-foreground">
                {grantSummary(grant.films, grant.kind, agency.name)}.
              </AlertDescription>
            </Alert>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Kind</dt>
              <dd>
                {GRANT_KINDS.find((option) => option.id === grant.kind)?.label}
              </dd>
              <dt className="text-muted-foreground">Goes to</dt>
              <dd>
                {grant.counter === "free"
                  ? "Free films (never unlock “Powered by” removal)"
                  : "Bought films (never expire)"}
              </dd>
              <dt className="text-muted-foreground">Reason</dt>
              <dd className="break-words">{grant.reason}</dd>
              <dt className="text-muted-foreground">Grant id</dt>
              <dd className="font-mono text-xs leading-5">{grant.grant_id}</dd>
            </dl>
            {mutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {studioBillingErrorMessage(mutation.error)} Retrying sends the
                  same grant id, so films are never added twice.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === "done" && grant && result && (
          <div className="flex flex-col gap-3">
            <Alert>
              <AlertDescription className="text-foreground">
                {result.granted
                  ? `${grantSummary(grant.films, grant.kind, agency.name).replace(/^Add/, "Added")}.`
                  : `Already applied: grant ${grant.grant_id} was recorded before, so nothing was added again.`}
              </AlertDescription>
            </Alert>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">
                The wallet now, as the bot returned it:
              </p>
              <WalletCounters wallet={result.wallet} />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "form" && (
            <>
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button onClick={review} disabled={!filmsValid || !reasonValid}>
                Review
              </Button>
            </>
          )}
          {step === "confirm" && grant && (
            <>
              <Button
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => setStep("form")}
              >
                Back
              </Button>
              <Button
                disabled={mutation.isPending}
                onClick={() => mutation.mutate(grant)}
              >
                {mutation.isPending
                  ? "Granting…"
                  : mutation.isError
                    ? "Retry"
                    : `Confirm: ${grantAction(grant.films, grant.kind)}`}
              </Button>
            </>
          )}
          {step === "done" && <Button onClick={close}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Free / plan / bought films and debt, as small tiles. */
export const WalletCounters = ({
  wallet,
}: {
  wallet: StudioWalletCounters;
}) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
    <CounterTile label="Free" value={wallet.free_credits} />
    <CounterTile label="Plan" value={wallet.plan_credits} />
    <CounterTile label="Bought" value={wallet.paid_credits} />
    <CounterTile
      label="Debt"
      value={wallet.debt}
      tone={wallet.debt > 0 ? "destructive" : undefined}
    />
  </div>
);

export const CounterTile = ({
  label,
  value,
  title,
  tone,
}: {
  label: string;
  value: number;
  title?: string;
  tone?: "destructive";
}) => (
  <div className="rounded-md border px-3 py-2" title={title}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div
      className={`text-lg font-semibold tabular-nums ${
        tone === "destructive" ? "text-destructive" : ""
      }`}
    >
      {value}
    </div>
  </div>
);
